import {useState} from 'react';
import {useIntl} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';

import FastModal from 'components/FastModal';
import FormattedHTMLMessage from 'components/FormattedHTMLMessage';
import {FmlxRadioButton} from 'fmlx-common-ui';
import {hideTypedModal} from 'stores/actions/typedModalAction';

import {handleInvalidPlateRecognition} from 'stores/business/plateRecognitionBusiness';
import {abortProtocol} from 'stores/business/instrumentBusiness';
import {getModalData} from 'stores/selectors/typedModalSelectors';
import {find} from 'lib/objectHelper';
import {HintType} from 'constants/UIComponentEnum';
import NestContent from './NestContent';

const DetectionOption = {
  SKIP: 'SKIP',
  RESOLVE: 'RESOLVE',
};

const ResolveOption = {
  CORRECT_PLATE: 'CORRECT_PLATE',
  REPLACE: 'REPLACE',
  IGNORE: 'IGNORE',
};

const Step = {
  DETECTION: 'DETECTION',
  RESOLVE: 'RESOLVE',
};

function ModalInvalidPlateRecognition() {
  const dispatch = useDispatch();
  const {formatMessage} = useIntl();

  const modalData = useSelector(getModalData);

  const {wrongDetection = [], trayType} = modalData;

  const invalidNestIds = wrongDetection.map((d) => d.position);
  const totalWrongDetections = wrongDetection.length;

  const [step, setStep] = useState(Step.DETECTION);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [detectionChoice, setDetectionChoice] = useState(null);
  const [resolveAnswers, setResolveAnswers] = useState({});

  const solvingNestId = invalidNestIds[currentIndex] ?? null;
  const expectedPlateID = wrongDetection[currentIndex]?.expectedPlateID ?? null;
  const availableLabwareState = useSelector((state) => state.availableLabwareState);
  const {name: expectedLabwareName} = find(availableLabwareState, {LabwareID: expectedPlateID});
  const isSolving = step === Step.RESOLVE;

  const goToNextStep = () => {
    if (step === Step.DETECTION) {
      if (detectionChoice === DetectionOption.SKIP) {
        dispatch(handleInvalidPlateRecognition(false));
        closeModal();
        return;
      }

      setStep(Step.RESOLVE);
      return;
    }

    if (currentIndex < totalWrongDetections - 1) {
      setCurrentIndex(currentIndex + 1);
      return;
    }

    const falseDetectionNestIds = getNestIdsByOption(resolveAnswers, ResolveOption.CORRECT_PLATE);
    const reScanNestIds = getNestIdsByOption(resolveAnswers, ResolveOption.REPLACE);
    dispatch(handleInvalidPlateRecognition(true, falseDetectionNestIds, reScanNestIds));
  };

  const goToPreviousStep = () => {
    if (currentIndex === 0) {
      setStep(Step.DETECTION);
      return;
    }
    setCurrentIndex(currentIndex - 1);
  };

  const handleAbortClick = () => {
    dispatch(abortProtocol(true));
    closeModal();
  };

  const getNestIdsByOption = (resolved, option) =>
    Object.entries(resolved)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, value]) => value === option)
      .map(([nestId]) => Number(nestId));

  const closeModal = () => dispatch(hideTypedModal(true));

  const isProceedDisabled = isSolving ? !resolveAnswers[solvingNestId] : !detectionChoice;

  const getProceedBtnText = () => {
    if (!isSolving) {
      return formatMessage({id: 'modal.InvalidPlateRecognition.button.proceed'});
    }
    if (currentIndex === totalWrongDetections - 1) {
      return formatMessage({id: 'modal.InvalidPlateRecognition.button.confirm'});
    }
    return formatMessage({id: 'modal.InvalidPlateRecognition.button.next'});
  };

  const detectionItems = [
    {
      content: formatMessage({id: 'modal.InvalidPlateRecognition.Detection.skip.title'}),
      hint: formatMessage({id: 'modal.InvalidPlateRecognition.Detection.skip.description'}),
      type: HintType.INFORMATION,
    },
    {
      content: formatMessage({id: 'modal.InvalidPlateRecognition.Detection.resolve.title'}),
      hint: formatMessage({id: 'modal.InvalidPlateRecognition.Detection.resolve.description'}),
      type: HintType.INFORMATION,
    },
  ];

  const resolveItems = [
    {content: formatMessage({id: 'modal.InvalidPlateRecognition.Resolve.correctPlate'})},
    {
      content: formatMessage({id: 'modal.InvalidPlateRecognition.Resolve.replacePlate.title'}),
      hint: solvingNestId ? `Expected plate in nest ${solvingNestId} - ${expectedLabwareName}` : '',
      type: HintType.INFORMATION,
    },
    {
      content: formatMessage({id: 'modal.InvalidPlateRecognition.Resolve.ignore.title'}),
      hint: formatMessage({id: 'modal.InvalidPlateRecognition.Resolve.ignore.description'}),
      type: HintType.INFORMATION,
    },
  ];

  const renderDetectionContent = (
    <div>
      <p>
        <FormattedHTMLMessage
          id="modal.InvalidPlateRecognition.Detection.title"
          values={{count: totalWrongDetections}}
        />
      </p>
      <p>{formatMessage({id: 'modal.InvalidPlateRecognition.action'})}</p>
      <FmlxRadioButton
        id="plate-recognition-detect"
        items={detectionItems}
        selectedIndex={Object.values(DetectionOption).indexOf(detectionChoice)}
        direction={FmlxRadioButton.Direction.COLUMN}
        onChange={({index}) => setDetectionChoice(Object.values(DetectionOption)[index])}
      />
    </div>
  );

  const renderResolveContent = (
    <div>
      <p>
        <FormattedHTMLMessage
          id="modal.InvalidPlateRecognition.Resolve.title"
          values={{count: `${currentIndex + 1}/${totalWrongDetections}`}}
        />
      </p>
      <p>{formatMessage({id: 'modal.InvalidPlateRecognition.action'})}</p>
      <FmlxRadioButton
        id="plate-recognition-resolve"
        items={resolveItems}
        selectedIndex={Object.values(ResolveOption).indexOf(resolveAnswers[solvingNestId] ?? null)}
        direction={FmlxRadioButton.Direction.COLUMN}
        onChange={({index}) => {
          const value = Object.values(ResolveOption)[index];
          setResolveAnswers((prev) => ({
            ...prev,
            [solvingNestId]: value,
          }));
        }}
      />
    </div>
  );

  const content = (
    <div style={{display: 'flex', minWidth: '200px', gap: '8px'}}>
      <NestContent
        trayType={trayType}
        invalidNestIds={invalidNestIds}
        isSolving={isSolving}
        solvingNestId={solvingNestId}
      />
      <div>{isSolving ? renderResolveContent : renderDetectionContent}</div>
    </div>
  );

  const buttons = [
    {
      className: 'btn-lg',
      style: 'primary',
      text: getProceedBtnText(),
      handlerClick: goToNextStep,
      disabled: isProceedDisabled,
    },
    {
      ...(isSolving ? {} : {style: 'primary'}),
      type: 'basic',
      text: formatMessage({id: 'modal.InvalidPlateRecognition.button.previous'}),
      handlerClick: goToPreviousStep,
    },
    {
      className: 'btn-lg',
      type: 'danger',
      text: formatMessage({id: 'modal.InvalidPlateRecognition.button.abort'}),
      handlerClick: handleAbortClick,
    },
  ];

  return (
    <FastModal
      show
      header={formatMessage({id: 'modal.InvalidPlateRecognition.title'})}
      content={content}
      btnAction={buttons}
      closeDisabled
      size="lg"
    />
  );
}

export default ModalInvalidPlateRecognition;
